import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, InputBase, IconButton, useTheme } from '@mui/material';
import { IconSearch, IconX } from '@tabler/icons-react';
import DocsSection from './DocsSection.jsx';
import { DOCS_SECTIONS } from '../../content/knowledgeBase.js';
import { fetchKbSections } from '../../content/kbClient.js';
import { useKbNarration } from './useKbNarration.js';
import { useGuidesOptional } from '../../user_guides/GuidesProvider.jsx';
import { DEFAULT_LOCALE } from '../../user_guides/contract';
import { tokenizeQuery, matchesAllTerms, scoreSection } from '../../utils/searchUtils.js';

/**
 * Shared knowledge-base renderer (#1064, search #1079). Drives both the full
 * /docs page (variant="page": left sticky table-of-contents rail beside the
 * content) and the dockable reference panel (variant="panel": stacked sections,
 * compact). A sticky search bar at the top of either view filters the sections
 * live by title + body text: the query is tokenized so a multi-word search
 * matches sections containing every term (any order, non-adjacent), surviving
 * sections are ranked by relevance, a result count is shown, and matched terms
 * are highlighted in-place (#1211).
 *
 * Scrollspy highlights the active section in the page rail via
 * IntersectionObserver; it is feature-detected so jsdom (tests) and reduced
 * environments simply render without active tracking.
 *
 * @param {object} props
 * @param {'page'|'panel'} [props.variant]
 * @param {React.RefObject} [props.scrollRef] scroll container for the panel
 *        variant's IntersectionObserver root; omit for window scrolling.
 */

/** Lowercased searchable text for a section: its title plus all block copy. */
const sectionText = (section) => {
  const parts = [section.title];
  for (const block of section.blocks) {
    if (block.text) parts.push(block.text);
    if (block.items) parts.push(...block.items);
    if (block.label) parts.push(block.label);
    if (block.body) parts.push(block.body);
  }
  return parts.join(' ').toLowerCase();
};

const SearchBar = ({ value, onChange, stickyTop, bg }) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  return (
    <Box
      sx={{
        position: 'sticky',
        top: stickyTop,
        zIndex: 2,
        backgroundColor: bg,
        pt: `${ds.space[4]}px`,
        pb: `${ds.space[4]}px`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: `${ds.space[3]}px`,
          border: `1px solid ${palette.divider}`,
          borderRadius: `${ds.radius.pill}px`,
          backgroundColor: palette.surface.raised,
          px: `${ds.space[4]}px`,
          py: `${ds.space[2]}px`,
        }}
      >
        <IconSearch size={16} style={{ color: palette.text.faint, flexShrink: 0 }} />
        <InputBase
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search the knowledge base"
          inputProps={{ 'aria-label': 'Search the knowledge base' }}
          sx={{
            flex: 1,
            fontSize: `${ds.font.row}px`,
            color: palette.text.primary,
            '& input::placeholder': { color: palette.text.faint, opacity: 1 },
          }}
        />
        {value && (
          <IconButton size="small" onClick={() => onChange('')} aria-label="Clear search">
            <IconX size={16} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const DocsBody = ({ variant = 'page', scrollRef }) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const compact = variant === 'panel';
  const [activeId, setActiveId] = useState(DOCS_SECTIONS[0]?.id);
  const [query, setQuery] = useState('');
  // Accordion state (#1083): sections start collapsed; an active search and TOC
  // jumps force-expand so matches/targets are readable.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const containerRef = useRef(null);

  // Locale-aware content + narration (#1088). When the guides provider is
  // present, load the active locale's published KB sections (translated copy +
  // per-section audio filenames) from the CDN, falling back to the bundled
  // en-US sections (which carry no audio, so no play control).
  const guides = useGuidesOptional();
  const voiceGender = guides?.voice ?? 'off';
  const locale = guides?.language ?? DEFAULT_LOCALE;
  const [remoteSections, setRemoteSections] = useState(null);

  useEffect(() => {
    if (!guides) return undefined;
    let cancelled = false;
    fetchKbSections(locale).then((secs) => {
      if (!cancelled && secs) setRemoteSections(secs);
    });
    return () => {
      cancelled = true;
    };
  }, [guides, locale]);

  const baseSections = remoteSections || DOCS_SECTIONS;
  const narration = useKbNarration({ locale, voiceGender });
  const audioAvailable = (section) =>
    voiceGender !== 'off' && section.audio && typeof section.audio[voiceGender] === 'string';

  const terms = useMemo(() => tokenizeQuery(query), [query]);
  const searching = terms.length > 0;
  // Keep sections matching every term, then rank by relevance (stable: original
  // order breaks score ties). Empty query leaves the catalog order untouched.
  const sections = useMemo(() => {
    if (!searching) return baseSections;
    return baseSections
      .map((s, i) => ({ s, i, score: scoreSection(sectionText(s), s.title, terms) }))
      .filter(({ s }) => matchesAllTerms(sectionText(s), terms))
      .sort((a, b) => b.score - a.score || a.i - b.i)
      .map(({ s }) => s);
  }, [searching, terms, baseSections]);

  const isExpanded = (id) => (searching ? true : expandedIds.has(id));
  const toggle = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const root = scrollRef?.current || null;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { root, rootMargin: '0px 0px -65% 0px', threshold: 0 },
    );
    const host = containerRef.current || document;
    // Section ids are controlled kebab-case (the KB package), so a
    // plain id selector is safe without CSS.escape.
    sections.forEach((s) => {
      const el = host.querySelector(`#${s.id}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [scrollRef, sections]);

  const jumpTo = (id) => {
    setExpandedIds((prev) => new Set(prev).add(id));
    const host = containerRef.current || document;
    const el = host.querySelector(`#${id}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setActiveId(id);
  };

  const stickyTop = compact ? 0 : { xs: 60, sm: 68 };
  const bg = compact ? palette.surface.overlay : palette.surface.page;

  const sectionList = (
    <Box ref={containerRef} sx={{ flex: 1, minWidth: 0, maxWidth: compact ? 'none' : '720px' }}>
      {sections.length === 0 ? (
        <Typography
          sx={{ mt: `${ds.space[8]}px`, fontSize: `${ds.font.row}px`, color: palette.text.dim }}
        >
          No results for &quot;{query.trim()}&quot;.
        </Typography>
      ) : (
        <>
          {searching && (
            <Typography
              sx={{
                mt: `${ds.space[4]}px`,
                mb: `${ds.space[2]}px`,
                fontSize: `${ds.font.meta}px`,
                color: palette.text.faint,
              }}
            >
              {sections.length === 1 ? '1 result' : `${sections.length} results`}
            </Typography>
          )}
          {sections.map((section) => (
            <DocsSection
              key={section.id}
              section={section}
              compact={compact}
              expanded={isExpanded(section.id)}
              onToggle={() => toggle(section.id)}
              searchTerms={terms}
              audioAvailable={audioAvailable(section)}
              playing={narration.playingId === section.id}
              onTogglePlay={() => narration.toggle(section)}
            />
          ))}
        </>
      )}
    </Box>
  );

  const search = <SearchBar value={query} onChange={setQuery} stickyTop={stickyTop} bg={bg} />;

  if (compact) {
    return (
      <>
        {search}
        {sectionList}
      </>
    );
  }

  return (
    <Box>
      {search}
      <Box sx={{ display: 'flex', gap: `${ds.space[10]}px`, alignItems: 'flex-start' }}>
        <Box
          component="nav"
          aria-label="Knowledge base contents"
          sx={{
            position: 'sticky',
            top: `${ds.space[14] + ds.space[10] + ds.space[8]}px`,
            flex: '0 0 auto',
            width: 200,
            display: { xs: 'none', md: 'block' },
            alignSelf: 'flex-start',
          }}
        >
          <Typography
            sx={{
              fontSize: `${ds.font.meta}px`,
              fontWeight: ds.font.weightMedium,
              color: palette.text.faint,
              textTransform: 'uppercase',
              letterSpacing: ds.font.letterUpper,
              mb: `${ds.space[3]}px`,
            }}
          >
            Contents
          </Typography>
          {sections.map((s) => {
            const active = s.id === activeId;
            return (
              <Box
                key={s.id}
                component="button"
                type="button"
                onClick={() => jumpTo(s.id)}
                sx={{
                  display: 'flex',
                  gap: `${ds.space[3]}px`,
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  padding: `${ds.space[2]}px 0`,
                  cursor: 'pointer',
                  fontSize: `${ds.font.row}px`,
                  lineHeight: 1.35,
                  color: active ? palette.interactive : palette.text.secondary,
                  fontWeight: active ? ds.font.weightMedium : ds.font.weightRegular,
                  '&:hover': { color: palette.interactive },
                  '&:focus-visible': {
                    outline: `1.5px solid ${palette.interactive}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ color: palette.text.faint, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                >
                  {s.number}
                </Box>
                <Box component="span">{s.title}</Box>
              </Box>
            );
          })}
        </Box>
        {sectionList}
      </Box>
    </Box>
  );
};

export default DocsBody;
